import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Eye, ImagePlus, History, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr, fmtDate } from "../api";
import { useAuth } from "../AuthContext";
import { StatusChip, Btn, ConfirmDialog, Modal, Field, inputCls, MediaPicker } from "../components";
import { projectFilters } from "../../data/content";

const CATS = projectFilters.filter((f) => f !== "All");
const STATUS_OPTIONS = ["Completed", "Ongoing"];

const RowsEditor = ({ rows, cols, onChange, testId }) => (
  <div className="space-y-2" data-testid={testId}>
    {rows.map((row, i) => (
      <div key={i} className="flex gap-2 items-start">
        {cols.map((c, j) => (
          <input key={j} className={inputCls} placeholder={c} value={row[j] || ""}
            data-testid={`${testId}-row-${i}-col-${j}`}
            onChange={(e) => onChange(rows.map((r, k) => (k === i ? r.map((v, m) => (m === j ? e.target.value : v)) : r)))} />
        ))}
        <button type="button" onClick={() => onChange(rows.filter((_, k) => k !== i))} className="p-2.5 text-slate-400 hover:text-red-600 shrink-0" data-testid={`${testId}-row-${i}-remove`}><Trash2 size={15} /></button>
      </div>
    ))}
    <Btn type="button" variant="ghost" onClick={() => onChange([...rows, cols.map(() => "")])} data-testid={`${testId}-add-row`}><Plus size={13} /> Add Row</Btn>
  </div>
);

const PreviewModal = ({ open, onClose, data }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] overflow-y-auto" data-testid="project-preview-modal">
      <div className="absolute inset-0 bg-[#0A1128]/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-w-3xl mx-auto my-8 bg-white rounded-sm shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white">
          <p className="font-display font-bold text-slate-900">Preview — Draft Content</p>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid="preview-close-btn"><X size={18} /></button>
        </div>
        <div className="p-8">
          <p className="font-mono-tech text-[10px] font-bold uppercase tracking-[0.25em] text-[#C5221F]">{data.client}</p>
          <h2 className="font-display mt-2 text-2xl font-extrabold text-slate-900">{data.title}</h2>
          <p className="mt-2 text-sm text-slate-500">{data.location}{data.year ? ` · ${data.year}` : ""} · {data.status_label}</p>
          {data.img && <img src={data.img} alt="" className="mt-6 w-full aspect-video object-cover border border-slate-200" />}
          <p className="mt-6 text-sm leading-relaxed text-slate-700 whitespace-pre-line">{data.scope}</p>
          {!!data.scopeList?.length && (
            <ul className="mt-5 space-y-2">
              {data.scopeList.map((s, i) => <li key={i} className="text-sm text-slate-700 border-l-2 border-[#C5221F] pl-3">{s}</li>)}
            </ul>
          )}
          {!!data.equipment?.length && (
            <table className="mt-6 w-full text-sm border border-slate-200">
              <tbody>
                {data.equipment.map((r, i) => (
                  <tr key={i} className={i % 2 ? "bg-white" : "bg-slate-50"}>
                    <td className="px-4 py-2.5 font-semibold border-t border-slate-200">{r[0]}</td>
                    <td className="px-4 py-2.5 text-slate-600 border-t border-slate-200">{r[1]}</td>
                    <td className="px-4 py-2.5 text-slate-800 border-t border-slate-200">{r[2]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!!data.gallery?.length && (
            <div className="mt-6 grid grid-cols-3 gap-2">
              {data.gallery.map((g, i) => <img key={i} src={g} alt="" className="aspect-video object-cover border border-slate-200" />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ProjectEditor = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState("");
  const [picker, setPicker] = useState(null); // 'cover' | 'gallery'
  const [preview, setPreview] = useState(false);
  const [delOpen, setDelOpen] = useState(false);
  const [revOpen, setRevOpen] = useState(false);
  const [scopeText, setScopeText] = useState("");

  useEffect(() => {
    adminApi.get(`/projects/${id}`)
      .then((r) => {
        setDoc(r.data);
        setData(r.data.data);
        setScopeText((r.data.data.scopeList || []).join("\n"));
      })
      .catch((e) => { toast.error(fmtErr(e)); navigate("/admin/projects"); });
  }, [id, navigate]);

  if (!doc || !data) return <div className="py-20 text-center text-slate-400"><Loader2 className="animate-spin mx-auto" /></div>;

  const set = (k, v) => { setData({ ...data, [k]: v }); setDirty(true); };
  const published = doc.status === "published";
  const liveDiffers = published && JSON.stringify(doc.published_data) !== JSON.stringify(data);

  const save = async () => {
    setBusy("save");
    try {
      const payload = { ...data, scopeList: scopeText.split("\n").map((s) => s.trim()).filter(Boolean) };
      const r = await adminApi.put(`/projects/${id}`, payload);
      setDoc(r.data);
      setData(r.data.data);
      setDirty(false);
      toast.success("Draft saved — the live website is unchanged until you publish");
      return true;
    } catch (e) {
      toast.error(fmtErr(e));
      return false;
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      if (dirty && !(await saveQuiet())) return;
      const r = await adminApi.post(`/projects/${id}/publish`);
      setDoc(r.data);
      toast.success("Published — this project is now live on the website");
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy("");
    }
  };

  const saveQuiet = async () => {
    try {
      const payload = { ...data, scopeList: scopeText.split("\n").map((s) => s.trim()).filter(Boolean) };
      const r = await adminApi.put(`/projects/${id}`, payload);
      setDoc(r.data); setData(r.data.data); setDirty(false);
      return true;
    } catch (e) { toast.error(fmtErr(e)); return false; }
  };

  const unpublish = async () => {
    setBusy("unpublish");
    try {
      const r = await adminApi.post(`/projects/${id}/unpublish`);
      setDoc(r.data);
      toast.success("Unpublished — removed from the public website");
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(""); }
  };

  const doDelete = async () => {
    setBusy("delete");
    try {
      await adminApi.delete(`/projects/${id}`);
      toast.success("Project deleted");
      navigate("/admin/projects");
    } catch (e) { toast.error(fmtErr(e)); setBusy(""); }
  };

  const restore = async (idx) => {
    try {
      const r = await adminApi.post(`/projects/${id}/revisions/${idx}/restore`);
      setDoc(r.data);
      setData(r.data.data);
      setScopeText((r.data.data.scopeList || []).join("\n"));
      setRevOpen(false);
      setDirty(true);
      toast.success("Revision restored into the draft — review and publish when ready");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="project-editor">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link to="/admin/projects" className="p-2 text-slate-400 hover:text-slate-700" data-testid="editor-back-link"><ArrowLeft size={18} /></Link>
          <div>
            <h1 className="font-display text-xl font-bold text-slate-900 line-clamp-1">{data.title || "Untitled Project"}</h1>
            <p className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
              <StatusChip status={doc.status} />
              {published && liveDiffers && <span className="text-amber-600 font-semibold">Unpublished changes</span>}
              {dirty && <span className="text-slate-400">· unsaved edits</span>}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Btn variant="ghost" onClick={() => setPreview(true)} data-testid="preview-project-btn"><Eye size={14} /> Preview</Btn>
          <Btn variant="ghost" onClick={save} disabled={busy === "save"} data-testid="save-draft-btn">{busy === "save" && <Loader2 size={13} className="animate-spin" />} Save Draft</Btn>
          {published ? (
            <Btn variant="dark" onClick={unpublish} disabled={!!busy} data-testid="unpublish-btn">Unpublish</Btn>
          ) : null}
          <Btn onClick={publish} disabled={!!busy} data-testid="publish-btn">{busy === "publish" && <Loader2 size={13} className="animate-spin" />} {published ? "Update Live Version" : "Publish"}</Btn>
        </div>
      </div>

      <div className="mt-6 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Project Title *"><input className={inputCls} value={data.title} onChange={(e) => set("title", e.target.value)} data-testid="field-title" /></Field>
              <Field label="Client Name *"><input className={inputCls} value={data.client} onChange={(e) => set("client", e.target.value)} data-testid="field-client" /></Field>
              <Field label="Location"><input className={inputCls} value={data.location} onChange={(e) => set("location", e.target.value)} data-testid="field-location" /></Field>
              <Field label="Year / Date"><input className={inputCls} value={data.year} onChange={(e) => set("year", e.target.value)} placeholder="e.g. 2023" data-testid="field-year" /></Field>
              <Field label="Project Status">
                <div className="flex gap-2">
                  <select className={inputCls} value={STATUS_OPTIONS.includes(data.status_label) ? data.status_label : "Completed"} onChange={(e) => set("status_label", e.target.value)} data-testid="field-status">
                    {STATUS_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </Field>
              <Field label="Categories">
                <div className="flex flex-wrap gap-1.5" data-testid="field-categories">
                  {CATS.map((c) => (
                    <button key={c} type="button"
                      onClick={() => set("categories", data.categories.includes(c) ? data.categories.filter((x) => x !== c) : [...data.categories, c])}
                      className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border rounded-sm transition-colors ${data.categories.includes(c) ? "bg-[#C5221F] border-[#C5221F] text-white" : "border-slate-300 text-slate-500 hover:border-slate-500"}`}
                      data-testid={`cat-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <Field label="Short Description / Scope of Work *" hint="Shown on project cards and the detail page.">
              <textarea rows={4} className={inputCls} value={data.scope} onChange={(e) => set("scope", e.target.value)} data-testid="field-scope" />
            </Field>
            <Field label="Scope Highlights" hint="One bullet per line.">
              <textarea rows={4} className={inputCls} value={scopeText} onChange={(e) => { setScopeText(e.target.value); setDirty(true); }} data-testid="field-scope-list" />
            </Field>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <Field label="Quick Specifications" hint="Shown in the expandable specs on project cards.">
              <RowsEditor rows={data.specs} cols={["Label (e.g. Weight)", "Value (e.g. 900 MT)"]} onChange={(r) => set("specs", r)} testId="specs" />
            </Field>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <Field label="Equipment & Package Details" hint="Full equipment table shown on the project detail page.">
              <RowsEditor rows={data.equipment} cols={["Equipment", "Tag / Unit", "Details (size · weight · MOC)"]} onChange={(r) => set("equipment", r)} testId="equipment" />
            </Field>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
            <Field label="Cover Photograph *">
              {data.img ? (
                <div className="relative group">
                  <img src={data.img} alt="" className="w-full aspect-video object-cover border border-slate-200" />
                  <div className="absolute inset-0 bg-[#0A1128]/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Btn variant="ghost" className="!bg-white" onClick={() => setPicker("cover")} data-testid="change-cover-btn">Change</Btn>
                    <Btn variant="danger" className="!bg-white" onClick={() => set("img", "")} data-testid="remove-cover-btn">Remove</Btn>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => setPicker("cover")} data-testid="pick-cover-btn"
                  className="w-full aspect-video border-2 border-dashed border-slate-300 hover:border-[#C5221F] flex flex-col items-center justify-center gap-2 text-slate-400 hover:text-[#C5221F] transition-colors">
                  <ImagePlus size={22} /><span className="text-xs font-bold uppercase tracking-widest">Choose Image</span>
                </button>
              )}
            </Field>
            <Field label={`Gallery Photographs (${data.gallery.length})`}>
              <div className="grid grid-cols-3 gap-2">
                {data.gallery.map((g, i) => (
                  <div key={i} className="relative group aspect-video">
                    <img src={g} alt="" className="w-full h-full object-cover border border-slate-200" />
                    <button type="button" onClick={() => set("gallery", data.gallery.filter((_, k) => k !== i))}
                      className="absolute top-1 right-1 bg-white/90 p-1 opacity-0 group-hover:opacity-100 transition-opacity text-red-600" data-testid={`gallery-remove-${i}`}><Trash2 size={12} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setPicker("gallery")} data-testid="add-gallery-btn"
                  className="aspect-video border-2 border-dashed border-slate-300 hover:border-[#C5221F] flex items-center justify-center text-slate-400 hover:text-[#C5221F] transition-colors">
                  <Plus size={18} />
                </button>
              </div>
            </Field>
            <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={data.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#C5221F]" data-testid="field-featured" />
              Feature on homepage
            </label>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-5">
            <Field label="SEO Title" hint="Optional — overrides the default browser title.">
              <input className={inputCls} value={data.seo_title} onChange={(e) => set("seo_title", e.target.value)} maxLength={200} data-testid="field-seo-title" />
            </Field>
            <Field label="Meta Description">
              <textarea rows={3} className={inputCls} value={data.seo_description} onChange={(e) => set("seo_description", e.target.value)} maxLength={400} data-testid="field-seo-desc" />
            </Field>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
            <button onClick={() => setRevOpen(true)} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-slate-500 hover:text-[#C5221F]" data-testid="revisions-open-btn">
              <History size={14} /> Revision History ({doc.revisions?.length || 0})
            </button>
            {user.role === "super_admin" && (
              <button onClick={() => setDelOpen(true)} className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-red-500 hover:text-red-700" data-testid="delete-project-btn">
                <Trash2 size={14} /> Delete Project
              </button>
            )}
            <p className="text-[11px] text-slate-400">Created by {doc.created_by} · {fmtDate(doc.created_at)}</p>
          </div>
        </div>
      </div>

      <MediaPicker open={picker === "cover"} onClose={() => setPicker(null)} onSelect={(url) => set("img", url)} />
      <MediaPicker open={picker === "gallery"} multiple onClose={() => setPicker(null)} onSelect={(urls) => set("gallery", [...data.gallery, ...urls.filter((u) => !data.gallery.includes(u))])} />
      <PreviewModal open={preview} onClose={() => setPreview(false)} data={{ ...data, scopeList: scopeText.split("\n").filter((s) => s.trim()) }} />
      <ConfirmDialog open={delOpen} title="Delete this project?" message={`"${data.title}" will be permanently removed. This cannot be undone.`} onConfirm={doDelete} onClose={() => setDelOpen(false)} busy={busy === "delete"} />

      <Modal open={revOpen} onClose={() => setRevOpen(false)} title="Revision History">
        {doc.revisions?.length ? (
          <ul className="space-y-3">
            {doc.revisions.map((r, i) => (
              <li key={i} className="flex items-center justify-between border border-slate-200 px-4 py-3 rounded-sm">
                <div>
                  <p className="text-sm font-semibold text-slate-800 line-clamp-1">{r.data?.title}</p>
                  <p className="text-xs text-slate-400">Published {fmtDate(r.at)} by {r.by}</p>
                </div>
                <Btn variant="ghost" onClick={() => restore(i)} data-testid={`restore-revision-${i}`}>Restore</Btn>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400">No previous versions yet. A snapshot is saved here every time the project is re-published.</p>
        )}
      </Modal>
    </div>
  );
};

export default ProjectEditor;
